import { Icon, IconButton, Paragraph, Title, Tooltip } from "@smartsuite/react-ui/lib";

import cx from "classnames";
import { type FC, type ReactNode, useRef, useState } from "react";
import type { FieldVisibilityCallback } from "../../hooks/useFormFieldVisibility";
import { type FormConfigItem, FormItemType } from "../../types/form";
import { $t } from "../../utils/intl";
import "./FormSectionItem.sass";
import { FormSectionItemText } from "./FormSectionItem.text";

export interface FormSectionItemProps {
  item: FormConfigItem;
  checkFieldVisibility: FieldVisibilityCallback;
  renderFormFieldItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormHeadingItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormHTMLBlockItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormImageItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormVideoItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormCalloutItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormDividerItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormRecaptchaItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormConsentItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
  renderFormPdfViewerItem: (
    item: FormConfigItem,
    fieldVisibilityCallback: FieldVisibilityCallback,
  ) => ReactNode;
}

export const FormSectionItem: FC<FormSectionItemProps> = ({
  item,
  checkFieldVisibility,
  renderFormFieldItem,
  renderFormHeadingItem,
  renderFormHTMLBlockItem,
  renderFormImageItem,
  renderFormVideoItem,
  renderFormCalloutItem,
  renderFormDividerItem,
  renderFormRecaptchaItem,
  renderFormConsentItem,
  renderFormPdfViewerItem,
}) => {
  const sectionLabelRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(true);

  // Check if the section has and conditions configured and enabled, and if the condition
  // is satisfied. If so, renders its title and caption. The fields that live in the
  // section are processed outside of this component, and rendered flat with the rest of
  // the form items, but using the same conditions as the section.
  const isItemConditionSatisfied = checkFieldVisibility(item.slug);
  if (!isItemConditionSatisfied) return null;

  const sectionLabel = item.params.label ? item.params.label : $t(FormSectionItemText.section);
  // A section caption has reached prod as a `{enabled, value}` help-text object (FORMS-WEB-YP);
  // rendering a non-string here throws and takes the whole form down.
  const sectionCaption = typeof item.params.caption === "string" ? item.params.caption : undefined;
  const sectionCaptionAsTooltip = item.params.help_text_as_tooltip;
  const sectionItems = item.params.items;

  return (
    <div className={cx("form-section-item", { "is-open": isOpen })}>
      <div className="form-section-item__header-wrapper">
        <button
          ref={sectionLabelRef}
          type="button"
          className="form-section-item__label"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-expanded={isOpen}
        >
          <Icon
            size="M"
            name="arrow-up-1"
            color="currentColor"
            className="form-section-item__arrow"
          />

          <Title size="xl" color="primary" weight="semibold">
            {sectionLabel}
          </Title>
        </button>
        {sectionCaption && sectionCaptionAsTooltip && (
          <div className="form-field-control__tooltip">
            <Tooltip
              text={sectionCaption}
              placements={["top"]}
              padding={2}
              target={<IconButton name="info-baloon" size="XS" ariaLabel={sectionLabel} />}
              triggerOpenElement={sectionLabelRef}
            />
          </div>
        )}
        {sectionCaption && !sectionCaptionAsTooltip && (
          <div className="form-section-item__caption">
            <Paragraph size="s" color="secondary">
              {sectionCaption}
            </Paragraph>
          </div>
        )}
      </div>
      {isOpen && !!sectionItems?.length && (
        <div className="form-section-item__items-wrapper">
          {sectionItems.map((item) => {
            if (item.type === FormItemType.heading)
              return renderFormHeadingItem(item, checkFieldVisibility);
            if (item.type === FormItemType.html_block)
              return renderFormHTMLBlockItem(item, checkFieldVisibility);
            if (item.type === FormItemType.field)
              return renderFormFieldItem(item, checkFieldVisibility);
            if (item.type === FormItemType.image)
              return renderFormImageItem(item, checkFieldVisibility);
            if (item.type === FormItemType.video)
              return renderFormVideoItem(item, checkFieldVisibility);
            if (item.type === FormItemType.callout)
              return renderFormCalloutItem(item, checkFieldVisibility);
            if (item.type === FormItemType.divider)
              return renderFormDividerItem(item, checkFieldVisibility);
            if (item.type === FormItemType.recaptcha) {
              return renderFormRecaptchaItem(item, checkFieldVisibility);
            }
            if (item.type === FormItemType.consent) {
              return renderFormConsentItem(item, checkFieldVisibility);
            }
            if (item.type === FormItemType.pdf_viewer) {
              return renderFormPdfViewerItem(item, checkFieldVisibility);
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};
